import axios from "axios";
import store from "./store";
import carrinhoSlice, { Produto } from "./slices/carrinhoSlice";
import { Quantidade } from "./produto";
import vanillaToast from "vanilla-toast/vanilla-toast";

interface StaleItem {
    index: number;
    resolved_quantidade: Quantidade | null;
}

interface CartValidationResponse {
    code: string;
    items: StaleItem[];
}

let syncInFlight: Promise<number> | null = null;
let syncQueued = false;

const getRemovedItemsMessage = (items: Produto[]): string => {
    const subjects = items.slice(0, 3).map((item) => item.title);

    if (items.length > 3) {
        subjects.push(`mais ${items.length - 3} itens`);
    }

    const subject = subjects.length === 1
        ? subjects[0]
        : `${subjects.slice(0, -1).join(", ")} e ${subjects[subjects.length - 1]}`;
    const verb = items.length === 1 ? "foi removido" : "foram removidos";

    return `${subject} ${verb} do carrinho porque ${items.length === 1 ? "está indisponível" : "estão indisponíveis"}.`;
};

const notifyRemovedItems = (items: Produto[]): void => {
    if (items.length === 0) {
        return;
    }

    vanillaToast.show(getRemovedItemsMessage(items), {
        className: "warning",
        duration: 5000,
        fadeDuration: 300,
    });
};

const getCartFingerprint = (itens: Produto[]): string =>
    itens
        .map((item) => `${item.id}:${item.quantidade}:${item.quantidade_produto?.id || "null"}`)
        .join("|");

export const reconciliarCarrinho = async (): Promise<number> => {
    if (syncInFlight) {
        return syncInFlight;
    }

    const { setItens } = carrinhoSlice.actions;

    syncInFlight = (async () => {
        const carrinho = store.getState();
        const itens = carrinho.itens || [];

        if (itens.length === 0) {
            return 0;
        }

        const fingerprint = getCartFingerprint(itens);
        const response = await axios.post<CartValidationResponse>("/api/carrinhos/validacoes", {
            produtos: itens,
        });

        const currentItens = store.getState().itens || [];

        if (getCartFingerprint(currentItens) !== fingerprint) {
            syncQueued = true;
            return 0;
        }

        const staleItems = response.data.items || [];

        if (staleItems.length === 0) {
            const normalized = currentItens.map((item) => ({
                ...item,
                desatualizado: false,
                desatualizado_motivo: undefined,
            }));

            store.dispatch(setItens({ itens: normalized }));
            return 0;
        }

        const byIndex = new Map<number, StaleItem>();
        staleItems.forEach((item) => byIndex.set(item.index, item));
        const removedItems: Produto[] = [];

        const updated: Produto[] = currentItens
            .map((item, index) => {
                const stale = byIndex.get(index);

                if (!stale) {
                    return {
                        ...item,
                        desatualizado: false,
                        desatualizado_motivo: undefined,
                    };
                }

                if (stale.resolved_quantidade) {
                    return {
                        ...item,
                        quantidade_produto: stale.resolved_quantidade,
                        desatualizado: false,
                        desatualizado_motivo: undefined,
                    };
                }

                removedItems.push(item);
                return null;
            })
            .filter((item): item is Produto => item !== null);

        store.dispatch(setItens({ itens: updated }));
        notifyRemovedItems(removedItems);
        return staleItems.length;
    })();

    let staleCount = 0;

    try {
        staleCount = await syncInFlight;
    } finally {
        syncInFlight = null;
    }

    if (syncQueued) {
        syncQueued = false;
        return reconciliarCarrinho();
    }

    return staleCount;
};
